import { useRef, useState, useEffect } from "react";

import classnames from "classnames";
import { useFormikContext } from "formik";
import { isPresent, slugify } from "neetocist";
import useOnClickOutside from "neetocommons/react-utils/useOnClickOutside";
import useHotkeys from "neetohotkeys";
import { Close } from "neetoicons";
import { Typography, Label, Button } from "neetoui";
import { Input } from "neetoui/formik";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";
import { useTranslation } from "react-i18next";

import { SIZES } from "./constants";
import useScrollIntoView from "./hooks/useScrollIntoView";
import "./toggleFeatureCard.scss";
import TooltipSwitch from "./ToolTipSwitch";

const ToggleFeatureCard = ({
  isCardStyleEnabled = true,
  title = "",
  description = null,
  switchName = "switch",
  titleDataTestid = "",
  switchDataTestid = "",
  descriptionDataTestid = "",
  isDisabled = false,
  children = null,
  inputProps,
  switchProps = {},
  editButtonProps = {},
  className = "",
}) => {
  const { t } = useTranslation();
  const cardRef = useRef();
  const id = slugify(title);

  const [isEditing, setIsEditing] = useState(false);

  const { setFieldValue, values, submitCount, errors, initialValues } =
    useFormikContext();

  useEffect(() => {
    if (!(submitCount >= 1 && isEmpty(errors))) return;
    setIsEditing(false);
  }, [submitCount]);

  useEffect(() => {
    if (isEmpty(errors)) return;
    setIsEditing(true);
  }, [values, errors]);

  const [shouldHighlight, setShouldHighlight] = useState(
    location.hash.split("#")[1] === id
  );

  const hasInput = values?.[switchName] && inputProps?.name;
  const enabledCondition = values?.[switchName];

  const handleInputReset = () => {
    setIsEditing(false);
    hasInput &&
      setFieldValue(inputProps?.name, initialValues?.[inputProps?.name]);
  };

  useOnClickOutside(cardRef, () => setShouldHighlight(false));

  useScrollIntoView({ elementId: id, trigger: shouldHighlight });

  const inputRef = useHotkeys("enter", () => setIsEditing(false), {
    mode: "scoped",
  });

  return (
    <div
      {...{ id }}
      data-testid="feature-card-container"
      ref={cardRef}
      className={classnames(
        "neeto-ui-bg-white neeto-ui-rounded-lg flex flex-col gap-y-2",
        { "border p-4": isCardStyleEnabled },
        { "neeto-ui-border-gray-300": !shouldHighlight },
        { "toggle-feature-card--highlighted": shouldHighlight },
        className
      )}
    >
      <Label className="flex flex-row items-center gap-2">
        <TooltipSwitch
          {...{
            enabledCondition,
            isDisabled,
            switchDataTestid,
            ...switchProps,
          }}
          name={switchName}
        />
        <Typography
          className="leading-snug"
          component="span"
          style="h4"
          weight="semibold"
          data-testid={
            isPresent(titleDataTestid)
              ? titleDataTestid
              : "toggle-feature-card-title"
          }
        >
          {title}
        </Typography>
      </Label>
      <Typography
        className="mb-4 whitespace-pre-line"
        style="body2"
        data-testid={
          isPresent(descriptionDataTestid)
            ? descriptionDataTestid
            : "toggle-feature-card-description"
        }
      >
        {description}
      </Typography>
      {hasInput && !isEditing && (
        <div className="m-0 flex flex-row items-center gap-x-2">
          {inputProps?.display}
          <Button
            label={t("neetoMolecules.common.actions.edit")}
            size="small"
            style="link"
            type="button"
            data-testid={
              isPresent(inputProps?.editButtonDataTestid)
                ? inputProps?.editButtonDataTestid
                : "toggle-feature-card-input-display-edit"
            }
            onClick={() => setIsEditing(true)}
            {...editButtonProps}
          />
        </div>
      )}
      {hasInput && isEditing && (
        <Input
          autoFocus
          label={inputProps?.label}
          name={inputProps?.name}
          placeholder={inputProps?.placeholder}
          ref={inputRef}
          size="medium"
          data-testid={
            isPresent(inputProps?.dataTestid)
              ? inputProps?.dataTestid
              : "toggle-feature-card-input-field"
          }
          suffix={
            <Button
              data-testid="toggle-feature-card-input-reset"
              icon={Close}
              size="small"
              style="text"
              type="button"
              onClick={handleInputReset}
            />
          }
          {...inputProps}
        />
      )}
      {children}
    </div>
  );
};

ToggleFeatureCard.propTypes = {
  /**
   * The title that will be displayed for the card.
   */
  title: PropTypes.string,
  /**
   * The descripton text for the particular feature or setting.
   */
  description: PropTypes.string,
  /**
   * The formik name of the feature or setting to be toggled. eg:
  {switchName: "enableEmails"}.
   */
  switchName: PropTypes.string,
  /**
   * Can be used to disable the toggle switch conditionally.
   */
  isDisabled: PropTypes.bool,
  /**
   * Can be used to enable the card appearance.
   */
  isCardStyleEnabled: PropTypes.bool,
  /**
   * Custom child components to be displayed in the card.
   */
  children: PropTypes.node,
  /**
   * Can be used to add an input field for a setting or a feature.
    For example, `Enable email notifications` card can have an email input field.
   */
  inputProps: PropTypes.shape({
    name: PropTypes.string,
    display: PropTypes.node,
    label: PropTypes.string,
    editButtonTooltip: PropTypes.string,
    editButtonDataTestid: PropTypes.string,
    placeholder: PropTypes.string,
    dataTestid: PropTypes.string,
    onChange: PropTypes.func,
    suffix: PropTypes.node,
    prefix: PropTypes.node,
    className: PropTypes.string,
    nakedInput: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(SIZES)),
    type: PropTypes.string,
    maxLength: PropTypes.number,
    contentSize: PropTypes.number,
    unlimitedChars: PropTypes.bool,
  }),
  /**
   * Can be used to add props to the switch component.
   */
  switchProps: PropTypes.shape({
    onChange: PropTypes.func,
    className: PropTypes.string,
    checked: PropTypes.bool,
    tooltipProps: PropTypes.shape({
      enabledSwitchText: PropTypes.string,
      disabledSwitchText: PropTypes.string,
    }),
  }),
  /**
   * Can be used to add props to the edit button component.
   */
  editButtonProps: PropTypes.shape({
    onClick: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.string,
    size: PropTypes.string,
    type: PropTypes.string,
    tooltipProps: PropTypes.object,
  }),
  /**
   * To specify external classnames as overrides to the ToggleFeatureCard wrapper.
   */
  className: PropTypes.string,
};

export default ToggleFeatureCard;
